import * as _ from 'lodash';
import * as React from 'react';
import { css } from 'aphrodite';
import { connect } from 'react-redux';
import { DataShape, Field, FormProps, reduxForm } from 'redux-form';

import { diagnostics } from '@blend/ui-common';
import history from 'login-utils/history';
import * as autoLoanUtils from 'login-utils/autoLoanUtils';
import * as specialtyVehicleForm from 'login-utils/specialtyVehicleForm';
import LargeIconButtonGroup from 'login-components/Common/LargeIconButtonGroup/LargeIconButtonGroup';
import { LargeIconButtonConfig } from 'login-components/Common/Buttons/LargeIconButton';
import SpecialtyVehicleBoatPurchaseSVG from 'login-components/Common/svg/SpecialtyVehicleBoatPurchaseSVG';
import SpecialtyVehicleBoatRefinanceSVG from 'login-components/Common/svg/SpecialtyVehicleBoatRefinanceSVG';
import SpecialtyVehicleRVPurchaseSVG from 'login-components/Common/svg/SpecialtyVehicleRVPurchaseSVG';
import SpecialtyVehicleRVRefinanceSVG from 'login-components/Common/svg/SpecialtyVehicleRVRefinanceSVG';
import SpecialtyVehiclePowersportPurchaseSVG from 'login-components/Common/svg/SpecialtyVehiclePowersportPurchaseSVG';
import SpecialtyVehiclePowersportRefinanceSVG from 'login-components/Common/svg/SpecialtyVehiclePowersportRefinanceSVG';
import LoadingSpinner from 'login-components/Common/LoadingSpinner/LoadingSpinner';
import * as autoLoansAPI from 'login-apis/autoLoansAPI';
import States from 'login-constants/states';
import { paneFormStyles } from 'login-components/Common/PaneLayout/paneStyles';
import ErrorMessage from 'login-components/Common/Notifications/ErrorMessage/ErrorMessage';

type BaseConfig = Pick<LargeIconButtonConfig, 'value' | 'buttonId' | 'text'>;

const baseConfig: Record<SpecialtyVehicleLoanPurposeType, BaseConfig> = {
  PURCHASE: {
    value: 'PURCHASE',
    buttonId: 'it-chooseSpecialtyVehicleLoanPurposePurchase',
    text: 'Purchase',
  },
  REFINANCE: {
    value: 'REFINANCE',
    buttonId: 'it-chooseSpecialtyVehicleLoanPurposeRefinance',
    text: 'Refinance',
  },
};

type IconAndSubtext = Pick<LargeIconButtonConfig, 'iconComponent' | 'subText'>;

const IconAndSubtextMap: Record<SpecialtyVehicleLoanPurposeType, Record<string, IconAndSubtext>> = {
  PURCHASE: {
    BOAT: {
      iconComponent: SpecialtyVehicleBoatPurchaseSVG,
      subText: 'Buy a boat.',
    },
    RV: {
      iconComponent: SpecialtyVehicleRVPurchaseSVG,
      subText: 'Buy a recreational vehicle.',
    },
    POWERSPORT: {
      iconComponent: SpecialtyVehiclePowersportPurchaseSVG,
      subText: 'Buy a motorcycle or powersport vehicle.',
    },
  },
  REFINANCE: {
    BOAT: {
      iconComponent: SpecialtyVehicleBoatRefinanceSVG,
      subText: `Change your boat's loan terms.`,
    },
    RV: {
      iconComponent: SpecialtyVehicleRVRefinanceSVG,
      subText: `Change your recreational vehicle's loan terms.`,
    },
    POWERSPORT: {
      iconComponent: SpecialtyVehiclePowersportRefinanceSVG,
      subText: 'Change the loan terms on your motorcycle or powersport vehicle.',
    },
  },
};

namespace SpecialtyVehicleLoanPurpose {
  export type Props = ReactRouter.RouteComponentProps<{}, {}> & {
    specialtyVehicleCategory: string;
  };
  export interface State {
    loanPurposeTypes: AutoLoanPurposeType[];
    error: string;
    isSubmitting: boolean;
  }
}

export class SpecialtyVehicleLoanPurpose extends React.Component<
  SpecialtyVehicleLoanPurpose.Props,
  SpecialtyVehicleLoanPurpose.State
> {
  state = {
    loanPurposeTypes: [],
    error: '',
    isSubmitting: false,
  };

  onSelect = async value => {
    if (autoLoanUtils.shouldCreateOrUpdateAutoLoan()) {
      const { specialtyVehicleCategory } = this.props;
      this.setState({
        error: '',
        isSubmitting: true,
      });

      try {
        await autoLoanUtils.createOrUpdateAutoLoan(
          {
            autoLoanPurposeType: value,
            specialtyVehicleCategory,
          },
          'SPECIALTY_VEHICLE',
        );
      } catch (err) {
        this.setState({
          error: 'Failed to create application, please try again',
          isSubmitting: false,
        });
      }
    } else {
      history.push(States.CREATE_ACCOUNT);
    }
  };

  async componentDidMount() {
    try {
      const loginAppConfig = await autoLoansAPI.getConfig();
      const loanPurposeTypes = loginAppConfig.SPECIALTY_VEHICLE.loanPurposeTypes;
      this.setState({ loanPurposeTypes });
    } catch (err) {
      // istanbul ignore next
      diagnostics.error(err);
    }
  }

  render(): React.ReactNode {
    const { specialtyVehicleCategory } = this.props;
    const { error, isSubmitting, loanPurposeTypes } = this.state;
    if (isSubmitting) {
      return (
        <div className={css(paneFormStyles.loadingSpinnerWrapper)}>
          <LoadingSpinner />
        </div>
      );
    }

    if (_.isEmpty(loanPurposeTypes)) return null;
    const specialtyVehicleLoanPurposeConfigs = _.map(loanPurposeTypes, (loanPurposeType: AutoLoanPurposeType) => ({
      ...baseConfig[loanPurposeType],
      ...IconAndSubtextMap[loanPurposeType][specialtyVehicleCategory],
    }));
    return (
      <React.Fragment>
        {error && (
          <div className={css(paneFormStyles.errorWrapper)}>
            <ErrorMessage errorText={error} />
          </div>
        )}
        <Field
          name={specialtyVehicleForm.FIELDS.autoLoanPurposeType}
          configs={specialtyVehicleLoanPurposeConfigs}
          onSelect={this.onSelect}
          component={LargeIconButtonGroup}
        />
      </React.Fragment>
    );
  }
}

const withReduxForm = reduxForm<
  FormProps<DataShape, SpecialtyVehicleLoanPurpose.Props, ReduxState>,
  SpecialtyVehicleLoanPurpose.Props,
  ReduxState
>({
  ...specialtyVehicleForm.FORM_CONFIG,
})(SpecialtyVehicleLoanPurpose);

export default connect((state: ReduxState) => ({
  specialtyVehicleCategory: specialtyVehicleForm.getSpecialtyVehicleCategory(state),
}))(withReduxForm);
